/**
 * THE CLOUD STRIP — one slim line about the saved league. D-EZ-99 / UI audit.
 *
 * It used to be a ~300 px banner in dark-on-dark gradient (contrast 1.16:1 on
 * Howard's device) on every screen, mid-race included. Now it is:
 *
 *   • ONE LINE, at most 56 px tall, black text on white — readable anywhere;
 *   • PLAIN WORDS — no "slot", no stamps; the full detail is in the Account area;
 *   • DISMISSIBLE FOR THE SESSION — the shell remembers the dismissal and does
 *     not bring it back on the next screen;
 *   • NEVER MID-RACE — the shell does not render it on the race or dice views.
 *
 * Whether to show anything at all, and how serious it is, is still decided by
 * `decideCloudNudge` (pure, tested). This component only words it shortly.
 */

import type { NudgeDecision, NudgeKind } from './cloudNudge.ts';

export interface CloudNudgeProps {
  decision: NudgeDecision;
  /** Open the Account area. Never loads by itself. */
  onShowAccount: () => void;
  /** Hide it for the rest of this session. */
  onDismiss: () => void;
}

/** Pure. The one line for each situation, in plain racing-game words. */
export function stripLine(kind: NudgeKind): string {
  switch (kind) {
    case 'signed-out':
      return 'Your league is kept on this device. Sign in to save it to your account too.';
    case 'cloud-unreadable':
      return "Couldn't check your saved league just now. Open Account to try again.";
    case 'no-cloud-save':
      return 'Nothing saved to your account yet — Save league keeps every season.';
    case 'load-first':
      return 'You have a saved league. Open Account to load it before you play.';
    case 'local-older-than-cloud':
    case 'local-newer-than-cloud':
    case 'local-same-age-as-cloud':
    case 'local-age-unknown':
      return 'Your saved league and this device hold different progress — check Account before you load.';
    case 'in-sync':
      return 'Your league is saved.';
  }
}

export default function CloudNudge({ decision, onShowAccount, onDismiss }: CloudNudgeProps) {
  if (!decision.show) return null;
  const line = stripLine(decision.kind);
  return (
    <aside
      className={`cloudstrip cloudstrip--${decision.level}`}
      role={decision.level === 'danger' ? 'alert' : 'status'}
      aria-label="Saved league"
    >
      <p className="cloudstrip__text" title={`${decision.heading}. ${decision.advice}`}>
        {line}
      </p>
      <button type="button" className="cloudstrip__go" onClick={onShowAccount}>
        Account
      </button>
      <button type="button" className="cloudstrip__x" onClick={onDismiss} aria-label="Dismiss for this session">
        ×
      </button>
    </aside>
  );
}
